/**
 * Internet outage detection data source.
 * Fetches internet disruption events from Cloudflare Radar annotations.
 * No API key required for the public outage feed.
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, Severity } from '../base-source';
import { getCountryCentroid } from '@/config/country-centroids';
import { djb2Hash } from '@/utils/hash';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'outages';

function outageTypeToSeverity(scope: string | undefined): Severity {
  switch (scope?.toLowerCase()) {
    case 'country': return 'critical';
    case 'region': return 'high';
    case 'city':
    case 'asn': return 'medium';
    default: return 'low';
  }
}

interface RadarOutage {
  id?: string;
  asn?: number;
  asnName?: string;
  asnOrgName?: string;
  scope?: string;
  outageType?: string;
  startDate: string;
  endDate?: string;
  locations?: string[];  // Country codes
  linkedUrl?: string;
  description?: string;
}

interface RadarOutagesResponse {
  annotations?: RadarOutage[];
  result?: {
    annotations?: RadarOutage[];
  };
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

export const outageSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'Internet Outages',
    description: 'Cloudflare Radar internet outage detection',
    icon: 'wifi-off',
    category: 'infrastructure',
    updateInterval: 300_000, // 5 min
    requiresApiKey: false,
    dataLicense: 'Cloudflare Terms',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] Internet outage source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      // Fetch outages from the last 7 days
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const url = `${getApiBase()}/api/data/outages?limit=100&dateStart=${weekAgo}`;
      const response = await fetch(url, { signal: AbortSignal.timeout(15_000) });

      if (!response.ok) {
        throw new Error(`Cloudflare Radar returned ${response.status}`);
      }

      const data: RadarOutagesResponse = await response.json();

      // Handle both top-level and nested result formats
      const annotations = data.annotations ?? data.result?.annotations ?? [];

      if (!Array.isArray(annotations)) {
        return [];
      }

      const events: NormalizedEvent[] = [];

      for (const outage of annotations) {
        const locations = outage.locations ?? [];
        const scope = outage.scope ?? 'unknown';
        const asnInfo = outage.asnOrgName ?? outage.asnName ?? (outage.asn ? `AS${outage.asn}` : '');

        // Build a descriptive title
        const locationStr = locations.join(', ') || 'Unknown location';
        const title = asnInfo
          ? `Internet outage: ${asnInfo} (${locationStr})`
          : `Internet outage: ${locationStr}`;

        const id = outage.id ?? djb2Hash(`${outage.startDate}-${locationStr}-${outage.asn ?? ''}`);

        const event: NormalizedEvent = {
          id: `outage-${id}`,
          source: SOURCE_ID,
          timestamp: outage.startDate,
          type: 'internet_outage',
          title,
          description: outage.description ?? undefined,
          severity: outageTypeToSeverity(scope),
          tags: ['internet-outage', 'cloudflare-radar', scope],
          links: outage.linkedUrl
            ? [{ url: outage.linkedUrl, label: 'Cloudflare Radar' }]
            : [],
          raw: {
            outageId: id,
            asn: outage.asn,
            asnName: outage.asnOrgName ?? outage.asnName,
            scope,
            outageType: outage.outageType,
            endDate: outage.endDate,
            locations,
          },
        };

        // Geo-locate using first country code
        if (locations.length > 0) {
          const centroid = getCountryCentroid(locations[0]);
          if (centroid) {
            event.location = {
              lat: centroid[0],
              lng: centroid[1],
              country: locations[0],
              countryCode: locations[0],
              name: locationStr,
            };
          }
        }

        events.push(event);
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
